import React, { useEffect, useId, useRef, useState } from "react";
import { Link, useLocation, useSearchParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { toast } from "react-toastify";
import {
  ArrowPathIcon,
  DocumentDuplicateIcon,
  ExclamationTriangleIcon,
  MagnifyingGlassIcon,
  PlusIcon,
  TableCellsIcon,
} from "@heroicons/react/24/outline";
import CustomDialog from "../modals/CustomDialog/CustomDialog";
import { Button } from "../forms/Buttons/Button";
import HelpButton from "../Help/HelpButton";
import VariantsGuide from "../Help/guides/VariantsGuide";
import FilterChips, { CHIP_TONES } from "../QuestionList/FilterChips";
import ResultsBar, { Pager } from "../QuestionList/ResultsBar";
import { createSubjectVariant, getSubjectVariants } from "../../services/subjectVariantService";
import { isCanceledError } from "../../utils/examError";
import { formatDate, formatDateTime } from "../../utils/formatDate";
import { useRowLink } from "../../utils/rowLink";
import { focusRing, linkButtonClass, Spinner, useMediaQuery } from "../../pages/Exams/TestBuilder/ui";
import {
  accountName,
  BookletChips,
  dialogFooterClass,
  fieldClass,
  LockedBadge,
  primaryButtonClass,
  secondaryButtonClass,
  statusTone,
  VARIANT_STATUSES,
  VariantStatusBadge,
} from "./variantUi";
import { variantErrorMessage } from "./variantText";
import { useErrorFocus } from "./VariantDialogs";

const PAGE_SIZE = 30;
const stateCardClass = "flex flex-col items-center justify-center rounded-2xl border border-slate-200 bg-white px-6 py-14 text-center shadow-sm";
const headerCell = (align) => `px-4 py-3 ${align} text-xs font-semibold uppercase tracking-wider text-slate-500`;
const listOf = (data) => (Array.isArray(data) ? data : data?.results || []);

// "18 / 20": what the variant has against what its grade's matrix plans; amber while short of it.
function Progress({ value, planned }) {
  const short = planned != null && value < planned;
  return (
    <span className="tabular-nums">
      <span className={`font-semibold ${short ? "text-amber-700" : "text-slate-900"}`}>{value ?? 0}</span>
      {planned != null && <span className="text-slate-500"> / {planned}</span>}
    </span>
  );
}

// "Confirmed by QA Moderator, 23.10.2026".
function ConfirmedLine({ variant, language }) {
  const [t] = useTranslation();
  if (variant.status !== "confirmed" || !variant.confirmed_at) return null;
  return (
    <p className="mt-1 text-xs text-slate-500">
      {t("variants.confirmed_by", { name: accountName(variant.confirmed_by) || "—", date: formatDate(variant.confirmed_at, language) })}
    </p>
  );
}

/**
 * "New variant": the grade (those of the subject's matrix) and, optionally, the name and the printed heading
 * (the subject's name when empty). Creates a draft and hands it to `onCreated`.
 */
function NewVariantDialog({ isOpen, folder, grades, defaultGrade, onClose, onCreated }) {
  const [t] = useTranslation();
  const id = useId();
  const formRef = useRef(null);
  const [grade, setGrade] = useState(null);
  const [name, setName] = useState("");
  const [heading, setHeading] = useState("");
  const [errors, setErrors] = useState({});
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!isOpen) return;
    setGrade(grades.some((g) => g.grade === defaultGrade) ? defaultGrade : grades[0]?.grade ?? null);
    setName("");
    setHeading("");
    setErrors({});
  }, [isOpen]); // eslint-disable-line react-hooks/exhaustive-deps

  const close = () => {
    if (!saving) onClose();
  };
  // After a refused create: the focus on the first field named (a grade's radio), else on the form's message.
  const focusError = useErrorFocus(formRef);

  const submit = async (e) => {
    e.preventDefault();
    if (grade == null) {
      setErrors({ grade: t("variants.create.grade_required") });
      focusError();
      return;
    }
    setSaving(true);
    setErrors({});
    try {
      const created = await createSubjectVariant({
        folder: folder.uuid,
        grade,
        ...(name.trim() ? { name: name.trim() } : {}),
        ...(heading.trim() ? { heading: heading.trim() } : {}),
      });
      onCreated(created);
    } catch (err) {
      const data = err?.response?.data;
      if (err?.response?.status === 400 && data && typeof data === "object" && !data.code) {
        const field = (key) => [].concat(data[key] || []).join(" ");
        setErrors({ grade: field("grade"), name: field("name"), heading: field("heading"), form: field("folder") || field("detail") });
      } else {
        setErrors({ form: variantErrorMessage(t, err, { fallbackKey: "variants.create.failed" }) });
      }
      focusError();
    } finally {
      setSaving(false);
    }
  };

  return (
    <CustomDialog isOpen={isOpen} closeModal={close} headerText={t("variants.create.title")} maxWidth="max-w-xl">
      <form ref={formRef} onSubmit={submit} noValidate>
        <p className="text-sm text-slate-500">{t("variants.create.text", { subject: folder.name })}</p>
        <fieldset className="mt-5">
          <legend className="mb-2 text-sm font-medium text-slate-700">{t("variants.create.grade")}</legend>
          <div className="grid gap-2 sm:grid-cols-2">
            {grades.map((g) => {
              const checked = grade === g.grade;
              return (
                <label
                  key={g.grade}
                  className={`flex cursor-pointer items-start gap-3 rounded-xl border px-3.5 py-3 transition duration-200 has-[:focus-visible]:outline has-[:focus-visible]:outline-2 has-[:focus-visible]:outline-offset-2 has-[:focus-visible]:outline-blue-600 ${
                    checked ? "border-blue-600 bg-blue-50/60 ring-1 ring-blue-600" : "border-slate-200 bg-white hover:border-slate-300"
                  }`}
                >
                  <input
                    type="radio"
                    name={`${id}-grade`}
                    value={g.grade}
                    checked={checked}
                    onChange={() => setGrade(g.grade)}
                    data-invalid={!!errors.grade && (checked || grade == null) ? "true" : undefined}
                    aria-describedby={errors.grade ? `${id}-grade-error` : undefined}
                    className="mt-0.5 h-4 w-4 border-slate-300 text-blue-600 focus:ring-0 focus:ring-offset-0"
                  />
                  <span className="min-w-0">
                    <span className="block text-sm font-semibold text-slate-900">{t("variants.grade", { grade: g.grade })}</span>
                    <span className="block text-xs text-slate-500">
                      {t("variants.create.grade_plan", {
                        count: g.planned?.count ?? 0,
                        pointsText: t("difficulty.points", { count: g.planned?.points ?? 0 }),
                      })}
                    </span>
                  </span>
                </label>
              );
            })}
          </div>
          {errors.grade && (
            <p id={`${id}-grade-error`} role="alert" className="mt-1.5 text-sm text-red-600">
              {errors.grade}
            </p>
          )}
        </fieldset>
        <div className="mt-5">
          <label htmlFor={`${id}-name`} className="mb-1.5 block text-sm font-medium text-slate-700">
            {t("variants.fields.name")} <span className="font-normal text-slate-500">({t("test_bank.form.optional")})</span>
          </label>
          <input
            id={`${id}-name`}
            type="text"
            value={name}
            maxLength={255}
            onChange={(e) => setName(e.target.value)}
            placeholder={t("variants.create.name_placeholder")}
            aria-invalid={!!errors.name}
            aria-describedby={errors.name ? `${id}-name-error` : undefined}
            className={`${fieldClass} h-10`}
          />
          {errors.name && (
            <p id={`${id}-name-error`} role="alert" className="mt-1.5 text-sm text-red-600">
              {errors.name}
            </p>
          )}
        </div>
        <div className="mt-4">
          <label htmlFor={`${id}-heading`} className="mb-1.5 block text-sm font-medium text-slate-700">
            {t("variants.fields.heading")} <span className="font-normal text-slate-500">({t("test_bank.form.optional")})</span>
          </label>
          <input
            id={`${id}-heading`}
            type="text"
            value={heading}
            maxLength={120}
            onChange={(e) => setHeading(e.target.value)}
            placeholder={t("variants.create.heading_placeholder")}
            aria-invalid={!!errors.heading}
            aria-describedby={`${id}-heading-hint`}
            className={`${fieldClass} h-10 ${errors.heading ? "border-red-300" : ""}`}
          />
          <p id={`${id}-heading-hint`} className="mt-1.5 text-xs text-slate-500">
            {errors.heading ? <span className="text-red-600">{errors.heading}</span> : t("variants.create.heading_hint")}
          </p>
        </div>
        {errors.form && (
          <p
            role="alert"
            tabIndex={-1}
            data-form-alert
            className="mt-4 flex items-start gap-2 rounded-xl border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-700 outline-none"
          >
            <ExclamationTriangleIcon className="mt-0.5 h-4 w-4 shrink-0" aria-hidden="true" />
            {errors.form}
          </p>
        )}
        <div className={dialogFooterClass}>
          <Button type="button" text={t("buttons.cancel")} color="secondary-outline" onClick={close} disabled={saving} />
          <Button type="submit" text={t("variants.create.submit")} color="primary" loading={saving} disabled={saving || !grades.length} />
        </div>
      </form>
    </CustomDialog>
  );
}

/**
 * The subject's variants, filtered by grade (those of its matrix) and status, a page at a time: a table on wide
 * screens, cards on phones. A variant opens in place (?variant=); "New variant" creates a draft and opens it (a
 * subject with no variant yet offers it in its empty card, without filters). The "?" after the title opens the
 * variants' guide (VariantsGuide, the same as a variant's).
 * The filters and the page are in the address (?vgrade=&vstatus=&vpage=), so they are still there when the list
 * comes back from a variant (its back link or the browser's Back) or the page is reloaded. `autoFocus`: the list
 * came back in this tab, so the focus goes to its title.
 */
export default function VariantList({ folder, matrix, onOpen, autoFocus = false }) {
  const [t, i18n] = useTranslation();
  const language = i18n.resolvedLanguage || i18n.language;
  const wide = useMediaQuery("(min-width: 768px)");
  const location = useLocation();
  const rowLink = useRowLink();
  const titleRef = useRef(null);
  const [searchParams, setSearchParams] = useSearchParams();
  const grades = matrix.grades;
  // A grade of the subject's matrix (numbers, as the chips compare them); any 1–12 while the matrix loads.
  const gradeParam = Number(searchParams.get("vgrade"));
  const grade =
    Number.isInteger(gradeParam) && gradeParam >= 1 && gradeParam <= 12 && (matrix.loading || grades.some((g) => g.grade === gradeParam))
      ? gradeParam
      : "all";
  const status = VARIANT_STATUSES.includes(searchParams.get("vstatus")) ? searchParams.get("vstatus") : "all";
  const page = Math.max(1, Math.floor(Number(searchParams.get("vpage"))) || 1);
  const [reloadKey, setReloadKey] = useState(0);
  // `empty`: the last answer said the subject has no variant at all (true), that it has some or a filter was on, or
  // failed (false); null until the first answer.
  const [state, setState] = useState({ loading: true, error: null, count: 0, results: [], empty: null });
  const [createOpen, setCreateOpen] = useState(false);

  // Back from a variant (or after deleting one): the focus on the list's title, after Headless UI gave it back.
  useEffect(() => {
    if (autoFocus) requestAnimationFrame(() => titleRef.current?.focus());
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // Filters changed (the others kept): "all" and page 1 leave the address, and a filter starts on page 1. Replaced,
  // not pushed: a chip is no step of the browser's history.
  const update = (changes) =>
    setSearchParams(
      (prev) => {
        const next = new URLSearchParams(prev);
        Object.entries(changes).forEach(([key, value]) => {
          if (value === "all" || value == null || (key === "vpage" && value === 1)) next.delete(key);
          else next.set(key, String(value));
        });
        if (!("vpage" in changes)) next.delete("vpage");
        return next;
      },
      { replace: true }
    );
  const setPage = (value) => update({ vpage: value });
  // react-router gives a new setSearchParams whenever the address changes: the list's load calls the latest.
  const updateRef = useRef(update);
  updateRef.current = update;

  useEffect(() => {
    const controller = new AbortController();
    const asked = grade !== "all" || status !== "all";
    setState((prev) => ({ ...prev, loading: true, error: null }));
    getSubjectVariants({
      params: {
        folder: folder.uuid,
        grade: grade !== "all" ? grade : undefined,
        status: status !== "all" ? status : undefined,
        page,
        page_size: PAGE_SIZE,
      },
      signal: controller.signal,
    })
      .then((data) => {
        const results = listOf(data);
        const count = typeof data?.count === "number" ? data.count : results.length;
        setState({ loading: false, error: null, count, results, empty: !asked && !count });
      })
      .catch((err) => {
        if (isCanceledError(err)) return;
        // A page that is no longer there (fewer variants than before): back to the first one.
        if (err?.response?.status === 404 && page > 1) {
          updateRef.current({ vpage: 1 });
          return;
        }
        setState({ loading: false, error: err, count: 0, results: [], empty: false });
      });
    return () => controller.abort();
  }, [folder.uuid, grade, status, page, reloadKey]);

  const plannedOf = (g) => grades.find((item) => item.grade === g)?.planned || null;
  const pageCount = Math.max(1, Math.ceil(state.count / PAGE_SIZE));
  const filtered = grade !== "all" || status !== "all";
  const hrefOf = (uuid) => {
    const params = new URLSearchParams(location.search);
    params.set("variant", uuid);
    return `${location.pathname}?${params.toString()}`;
  };
  const matrixHref = (() => {
    const params = new URLSearchParams(location.search);
    params.set("tab", "matrix");
    params.delete("variant");
    return `${location.pathname}?${params.toString()}`;
  })();
  const changeGrade = (value) => update({ vgrade: value });
  const changeStatus = (value) => update({ vstatus: value });
  const clearFilters = () => update({ vgrade: "all", vstatus: "all" });
  const handleCreated = (created) => {
    setCreateOpen(false);
    toast.success(t("variants.toast.created"));
    if (created?.uuid) onOpen(created.uuid);
  };

  const canCreate = !matrix.loading && grades.length > 0;
  // The matrix has loaded and has no grade: nothing can be created until it is filled.
  const noMatrix = !matrix.loading && !matrix.error && !grades.length;
  // The subject has no variant yet: its card offers "New variant" (the header's is not repeated) and there is
  // nothing to filter. Decided by the answers (state.empty), so neither shows while the first one loads (to go away
  // when it is empty), nor goes away while a filter changes.
  const noVariants = !filtered && state.empty !== false;
  // A grade filter is worth its row with two grades or more (or one set from the address).
  const gradeChips = grades.length > 1 || grade !== "all";
  const createButton = (
    <button type="button" onClick={() => setCreateOpen(true)} disabled={!canCreate} className={primaryButtonClass}>
      <PlusIcon className="h-5 w-5" aria-hidden="true" />
      {t("variants.list.create")}
    </button>
  );

  const nameLink = (variant, className) => (
    <Link to={hrefOf(variant.uuid)} className={`rounded text-slate-900 transition-colors duration-200 hover:text-blue-700 ${focusRing} ${className}`}>
      {variant.name || "—"}
    </Link>
  );

  let body;
  if (state.loading && !state.results.length) {
    body = (
      <div className={stateCardClass} role="status">
        <Spinner className="mb-3 h-8 w-8" />
        <p className="text-sm text-slate-500">{t("variants.list.loading")}</p>
      </div>
    );
  } else if (state.error) {
    body = (
      <div className={stateCardClass} role="alert">
        <div className="mb-3 rounded-full bg-red-50 p-3 text-red-500">
          <ExclamationTriangleIcon className="h-6 w-6" aria-hidden="true" />
        </div>
        <h4 className="text-sm font-medium text-red-700">{variantErrorMessage(t, state.error, { fallbackKey: "variants.list.load_failed" })}</h4>
        <button type="button" onClick={() => setReloadKey((n) => n + 1)} className={`${secondaryButtonClass} mt-4`}>
          <ArrowPathIcon className="h-4 w-4" aria-hidden="true" />
          {t("test_bank.builder.retry")}
        </button>
      </div>
    );
  } else if (!state.results.length) {
    // As the answer says (not the filters, which change a moment before the next load starts).
    body = !state.empty ? (
      <div className={stateCardClass}>
        <div className="mb-3 rounded-full bg-slate-100 p-3 text-slate-400">
          <MagnifyingGlassIcon className="h-6 w-6" aria-hidden="true" />
        </div>
        <h4 className="text-sm font-medium text-slate-900">{t("variants.list.not_found")}</h4>
        <button type="button" onClick={clearFilters} className={`${secondaryButtonClass} mt-4`}>
          {t("question_list.clear_all")}
        </button>
      </div>
    ) : (
      <div className={stateCardClass}>
        <div className="mb-3 rounded-full bg-slate-100 p-3 text-slate-400">
          <DocumentDuplicateIcon className="h-6 w-6" aria-hidden="true" />
        </div>
        <h4 className="text-sm font-medium text-slate-900">{t("variants.list.empty")}</h4>
        {/* No matrix yet: the way is to fill it (the button above is off). A failed matrix has its own message. */}
        {noMatrix ? (
          <>
            <p className="mt-1 max-w-lg text-sm text-slate-500">{t("variants.list.no_matrix")}</p>
            <Link to={matrixHref} className={`${secondaryButtonClass} mt-4`}>
              <TableCellsIcon className="h-4 w-4" aria-hidden="true" />
              {t("variants.list.open_matrix")}
            </Link>
          </>
        ) : (
          !matrix.error && (
            <>
              <p className="mt-1 max-w-lg text-sm text-slate-500">{t("variants.list.empty_hint")}</p>
              {canCreate && <div className="mt-4">{createButton}</div>}
            </>
          )
        )}
      </div>
    );
  } else if (wide) {
    body = (
      // relative: the sr-only texts inside stay in the scroll box.
      <div className={`relative min-w-0 overflow-x-auto rounded-2xl border border-slate-200 bg-white shadow-sm ${state.loading ? "opacity-60" : ""}`}>
        <table className="min-w-full divide-y divide-slate-200">
          <thead className="bg-slate-50">
            <tr>
              <th scope="col" className={headerCell("text-left")}>{t("variants.list.col_variant")}</th>
              <th scope="col" className={headerCell("text-left")}>{t("variants.list.col_grade")}</th>
              <th scope="col" className={headerCell("text-left")}>{t("variants.list.col_status")}</th>
              <th scope="col" className={headerCell("text-right")}>{t("variants.list.col_questions")}</th>
              <th scope="col" className={headerCell("text-right")}>{t("variants.list.col_points")}</th>
              <th scope="col" className={headerCell("text-left")}>{t("variants.list.col_booklets")}</th>
              <th scope="col" className={headerCell("text-left")}>{t("variants.list.col_updated")}</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-100 bg-white">
            {state.results.map((variant) => {
              const planned = plannedOf(variant.grade);
              return (
                <tr key={variant.uuid} {...rowLink(hrefOf(variant.uuid))} className="cursor-pointer align-top transition-colors duration-150 hover:bg-slate-50/80">
                  <td className="px-4 py-3 text-sm">
                    <div className="min-w-[16rem] max-w-2xl">
                      <div className="flex flex-wrap items-center gap-2">
                        {nameLink(variant, "font-medium")}
                        {variant.locked && <LockedBadge />}
                      </div>
                      {variant.heading && (
                        <p className="mt-0.5 truncate text-xs text-slate-500" title={variant.heading}>
                          {t("variants.heading_line", { heading: variant.heading })}
                        </p>
                      )}
                    </div>
                  </td>
                  <td className="whitespace-nowrap px-4 py-3 text-sm text-slate-700">{t("variants.grade", { grade: variant.grade })}</td>
                  <td className="whitespace-nowrap px-4 py-3 text-sm">
                    <VariantStatusBadge status={variant.status} />
                    <ConfirmedLine variant={variant} language={language} />
                  </td>
                  <td className="whitespace-nowrap px-4 py-3 text-right text-sm">
                    <Progress value={variant.questions_count} planned={planned?.count} />
                  </td>
                  <td className="whitespace-nowrap px-4 py-3 text-right text-sm">
                    <Progress value={variant.points_total} planned={planned?.points} />
                  </td>
                  <td className="px-4 py-3 text-sm">
                    {variant.booklets?.length ? <BookletChips booklets={variant.booklets} limit={3} className="max-w-md" /> : <span className="text-slate-300">—</span>}
                  </td>
                  <td className="whitespace-nowrap px-4 py-3 text-sm tabular-nums text-slate-500">{formatDateTime(variant.updated, language)}</td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    );
  } else {
    body = (
      <ul className={`flex flex-col gap-2.5 ${state.loading ? "opacity-60" : ""}`}>
        {state.results.map((variant) => {
          const planned = plannedOf(variant.grade);
          return (
            <li
              key={variant.uuid}
              {...rowLink(hrefOf(variant.uuid))}
              className="cursor-pointer rounded-2xl border border-slate-200 bg-white p-3.5 shadow-sm transition duration-200 hover:border-slate-300"
            >
              <div className="flex items-start justify-between gap-2">
                {nameLink(variant, "min-w-0 text-[15px] font-semibold leading-snug [overflow-wrap:anywhere]")}
                <VariantStatusBadge status={variant.status} />
              </div>
              <p className="mt-1 text-sm text-slate-600">
                {[t("variants.grade", { grade: variant.grade }), variant.heading].filter(Boolean).join(" · ")}
              </p>
              <p className="mt-1 flex flex-wrap gap-x-4 gap-y-1 text-sm text-slate-600">
                <span>
                  {t("variants.list.col_questions")}: <Progress value={variant.questions_count} planned={planned?.count} />
                </span>
                <span>
                  {t("variants.list.col_points")}: <Progress value={variant.points_total} planned={planned?.points} />
                </span>
              </p>
              {(variant.locked || variant.booklets?.length > 0) && (
                <div className="mt-2 flex flex-wrap items-center gap-1.5">
                  {variant.locked && <LockedBadge />}
                  <BookletChips booklets={variant.booklets} limit={3} />
                </div>
              )}
              <ConfirmedLine variant={variant} language={language} />
              <p className="mt-1 text-xs text-slate-500">{t("variants.updated", { date: formatDateTime(variant.updated, language) })}</p>
            </li>
          );
        })}
      </ul>
    );
  }

  return (
    // relative: the sr-only texts inside (the booklets' "+N") stay in <main>'s scroll box.
    <section aria-labelledby="variants-title" className="relative min-w-0 space-y-4">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div className="min-w-0">
          {/* The guide's "?" right after the title (HelpTitle's section size; the title keeps its id and focus). */}
          <div className="flex min-w-0 items-center gap-1.5">
            <h3 id="variants-title" ref={titleRef} tabIndex={-1} className="min-w-0 text-lg font-semibold text-slate-900 outline-none">
              {t("variants.list.title")}
            </h3>
            <HelpButton id="variants" title={t("help.variants.title")} className="-my-2 md:-my-1">
              <VariantsGuide />
            </HelpButton>
          </div>
          <p className="mt-0.5 max-w-3xl text-sm text-slate-500">{t("variants.list.subtitle")}</p>
        </div>
        {!noVariants && createButton}
      </div>

      {matrix.error ? (
        <div role="alert" className="flex flex-wrap items-center gap-2 rounded-xl border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700">
          <ExclamationTriangleIcon className="h-5 w-5 shrink-0" aria-hidden="true" />
          {t("variants.list.matrix_failed")}
          <button type="button" onClick={matrix.retry} className={linkButtonClass}>
            {t("test_bank.builder.retry")}
          </button>
        </div>
      ) : (
        // The empty list's card says it (with the same link) when it is shown; nothing before the first answer.
        noMatrix &&
        state.empty === false && (
          <div className="flex flex-wrap items-center gap-x-3 gap-y-2 rounded-xl border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-900">
            <TableCellsIcon className="h-5 w-5 shrink-0 text-amber-600" aria-hidden="true" />
            <span className="min-w-0 flex-1">{t("variants.list.no_matrix")}</span>
            <Link to={matrixHref} className={linkButtonClass}>
              {t("variants.list.open_matrix")}
            </Link>
          </div>
        )
      )}

      {!noVariants && (
        <div className="flex flex-col gap-3 xl:flex-row xl:items-center xl:gap-8">
          {gradeChips && (
            <div className="min-w-0 sm:flex sm:items-center sm:gap-3">
              <p className="mb-1.5 text-xs font-semibold uppercase tracking-wider text-slate-500 sm:mb-0 sm:shrink-0">{t("variants.list.col_grade")}</p>
              <FilterChips
                label={t("variants.list.grade_filter")}
                allLabel={t("variants.list.all_grades")}
                items={grades.map((g) => ({ value: g.grade, label: t("variants.grade", { grade: g.grade }) }))}
                selected={grade}
                onSelect={changeGrade}
              />
            </div>
          )}
          <div className="min-w-0 sm:flex sm:items-center sm:gap-3">
            <p className="mb-1.5 text-xs font-semibold uppercase tracking-wider text-slate-500 sm:mb-0 sm:shrink-0">{t("variants.list.col_status")}</p>
            <FilterChips
              label={t("variants.list.status_filter")}
              allLabel={t("variants.list.all_statuses")}
              items={VARIANT_STATUSES.map((value) => ({ value, label: t(`variants.status.${value}`), tone: statusTone(value) || CHIP_TONES.slate }))}
              selected={status}
              onSelect={changeStatus}
            />
          </div>
        </div>
      )}

      {!state.error && (state.results.length > 0 || (state.loading && state.empty === false)) && (
        <ResultsBar
          countText={t("variants.list.count", { count: state.count })}
          page={page}
          pageCount={pageCount}
          onPage={setPage}
          loading={state.loading}
        />
      )}

      {body}

      {state.results.length > 0 && (
        <div className="flex justify-center">
          <Pager page={page} pageCount={pageCount} onPage={setPage} disabled={state.loading} label={t("question_list.pagination_bottom")} />
        </div>
      )}

      <NewVariantDialog
        isOpen={createOpen}
        folder={folder}
        grades={grades}
        defaultGrade={grade === "all" ? null : grade}
        onClose={() => setCreateOpen(false)}
        onCreated={handleCreated}
      />
    </section>
  );
}
